import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import TextAreaWithSuggestions from "@/pages/(app)/returns/refund/validate/text-area-with-suggestion";

import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { ArticleItem } from "../article-item/article-item";
import { useLineItemRejectReasons } from "@/pages/(app)/returns/refund/hooks/use-line-item-reject-reason";
import { useMemo } from "react";
import useTranslation from "@babackapp/translations/use-translation";
import { useRejectLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-list-section";
import useRejectLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-quantities-summary";
import { useRejectLineItem } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-context";
import {
  useSetNotTreatedByMerchant,
  useNotTreatedByMerchant,
} from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-state-context";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import useGetItemsInActiveReturns from "@babackapp/private-api-client/client/use-get-items-in-active-returns";
import { useConflictingItems } from "@/pages/(app)/returns/hooks/use-conflicting-items";
import { ConflictWarning } from "@/pages/(app)/returns/components/conflict-warning";

export default function ArticlesToHandleByMerchant() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const { itemsToShowToHandle } = useRejectLineItemsListSections();
  const isAdmin = useViewerHasAtLeastAdminRights();
  const summary = useRejectLineItemsQuantitiesSummary({
    state: "not-treated-by-merchant",
  });

  const {
    data: itemsInActiveReturns,
  } = useGetItemsInActiveReturns({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
  });

  if (summary.max <= 0 || !isAdmin) return null;

  return (
    <AccordionItem value="toHandleByMerchant">
      <AccordionTrigger>
        <span>
          {t("misc.todo")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsToShowToHandle.map((lineItem, index) => (
            <ToHandleByMerchantArticle
              key={index + lineItem.line_item_id}
              lineItem={lineItem}
              itemsInActiveReturns={itemsInActiveReturns}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function ToHandleByMerchantArticle({
  lineItem,
  itemsInActiveReturns,
}: {
  lineItem: ReturnRequestLineItemDto;
  itemsInActiveReturns?: { itemsInOtherReturns: Array<{ lineItemId: string; quantity: number; returnRequestId: string; returnRequestName: string; }> };
}) {
  const { data: shop } = useCurrentShopSuspense();
  const { item, updateResolution, updateVariant } = useRejectLineItem(lineItem);
  const quantityValue = useNotTreatedByMerchant(lineItem.line_item_id);
  const updateQuantity = useSetNotTreatedByMerchant(lineItem.line_item_id);
  const { addLineItemReason, lineItemsReason } = useLineItemRejectReasons();
  const editineItemReason = addLineItemReason(lineItem.line_item_id);

  const selectLineItemReason = useMemo(() => {
    return lineItemsReason[lineItem.line_item_id]?.reason;
  }, [lineItemsReason, lineItem.line_item_id]);

  const {
    conflictingItems,
    adjustedMaxQuantity,
    isDisabled,
    warningMessage,
  } = useConflictingItems({
    lineItemId: lineItem.line_item_id,
    itemsInActiveReturns: itemsInActiveReturns?.itemsInOtherReturns,
    currentQuantity: quantityValue.selectedQuantity,
    maxQuantity: quantityValue.maxQuantity,
  });

  const hasConflict = conflictingItems.length > 0;

  // Get unique returns for the component
  const uniqueReturns = Array.from(
    new Map(conflictingItems.map(item => [item.returnRequestId, item])).values()
  );

  const displayMessage = hasConflict && warningMessage ? (
    <ConflictWarning
      warningMessage={warningMessage}
      conflictingReturns={uniqueReturns}
      adjustedMaxQuantity={adjustedMaxQuantity}
      shopId={shop.id}
    />
  ) : undefined;

  return (
    <div>
      <ArticleItem
        lineItem={item}
        quantity={quantityValue.selectedQuantity}
        maxQuantity={adjustedMaxQuantity}
        onChangeQuantity={updateQuantity}
        onChangeVariant={updateVariant}
        onChangeResolution={updateResolution}
        hideStatus
        hideResolution
        disabled={isDisabled}
        disabledReason={displayMessage}
      />
      {quantityValue.selectedQuantity > 0 && (
        <TextAreaWithSuggestions
          onChangeReason={editineItemReason}
          reason={selectLineItemReason}
        />
      )}
    </div>
  );
}
